<template>
  <nav class="navbar sticky top-0 z-50 backdrop-blur-md bg-brand-dark/80 border-b border-pyth-purple/20">
    <div class="container mx-auto px-6 py-4">
      <div class="flex items-center justify-between">
        <!-- Left Side: Logo & Navigation Links -->
        <div class="flex items-center gap-8">
          <!-- Logos -->
          <div class="flex items-center gap-3">
            <a href="https://magiceden.io/marketplace/pythenians" target="_blank" rel="noopener noreferrer" class="hover:opacity-80 transition-opacity">
              <img :src="logos.v02.white" alt="Pythenians" class="h-10 w-auto" />
            </a>
            <a href="https://www.pyth.network/" target="_blank" rel="noopener noreferrer" class="hidden md:block hover:opacity-80 transition-opacity">
              <img :src="branding.symbol.light.svg" alt="Pyth Network" class="h-6 w-auto opacity-80" />
            </a>
          </div>

          <!-- Desktop Navigation Links -->
          <div class="hidden md:flex items-center gap-6">
            <router-link
              to="/"
              class="nav-link"
              :class="{ 'active': $route.path === '/' }"
            >
              Home
            </router-link>

            <router-link
              v-if="isAuthenticated"
              to="/profile"
              class="nav-link"
              :class="{ 'active': $route.path === '/profile' }"
            >
              Profile
            </router-link>

            <router-link
              v-if="walletConnected && hasNFT && isAdmin"
              to="/admin"
              class="nav-link"
              :class="{ 'active': $route.path === '/admin' }"
            >
              Admin
            </router-link>
          </div>
        </div>

        <!-- Right Side: Wallet Status -->
        <div class="hidden md:flex items-center gap-3">
          <!-- Connect Wallet Button (when not connected) -->
          <button
            v-if="!walletConnected"
            @click="handleConnectWallet"
            class="flex items-center gap-2 px-5 py-2.5 bg-[#7731EA] text-white text-sm font-medium rounded-lg hover:bg-[#6521D0] transition-colors border border-[#7731EA]/20"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 18V19C21 20.1 20.1 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3H19C20.1 3 21 3.9 21 5V6H12C10.89 6 10 6.9 10 8V16C10 17.1 10.89 18 12 18H21ZM12 16H22V8H12V16ZM16 13.5C15.17 13.5 14.5 12.83 14.5 12C14.5 11.17 15.17 10.5 16 10.5C16.83 10.5 17.5 11.17 17.5 12C17.5 12.83 16.83 13.5 16 13.5Z" fill="currentColor"/>
            </svg>
            Connect wallet
          </button>

          <!-- Wallet Status (when connected) -->
          <div v-else class="flex items-center gap-3">
            <router-link to="/profile" class="px-4 py-2 bg-brand-dark-lighter rounded-lg border border-pyth-purple/30 hover:border-pyth-purple transition-colors cursor-pointer">
              <span class="text-xs text-gray-400">Wallet</span>
              <p class="text-white font-mono text-sm">{{ truncatedAddress }}</p>
            </router-link>
            <button
              @click="handleDisconnect"
              class="px-4 py-2 bg-red-600/20 text-red-400 border border-red-600/50 rounded-lg hover:bg-red-600/30 transition-colors text-sm focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-brand-dark"
            >
              Disconnect
            </button>
          </div>
        </div>

        <!-- Mobile Menu Button -->
        <button
          @click="toggleMobileMenu"
          class="md:hidden p-2 text-white hover:text-pyth-purple transition-colors"
        >
          <svg v-if="!mobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Mobile Menu -->
      <div
        v-if="mobileMenuOpen"
        class="md:hidden mt-4 pt-4 border-t border-pyth-purple/20 space-y-3"
      >
        <router-link
          to="/"
          class="mobile-nav-link"
          :class="{ 'active': $route.path === '/' }"
          @click="closeMobileMenu"
        >
          Home
        </router-link>

        <router-link
          v-if="isAuthenticated"
          to="/profile"
          class="mobile-nav-link"
          :class="{ 'active': $route.path === '/profile' }"
          @click="closeMobileMenu"
        >
          Profile
        </router-link>

        <router-link
          v-if="isAuthenticated && isAdmin"
          to="/admin"
          class="mobile-nav-link"
          :class="{ 'active': $route.path === '/admin' }"
          @click="closeMobileMenu"
        >
          Admin
        </router-link>

        <!-- Mobile Wallet Status -->
        <div class="pt-3 space-y-2">
          <!-- Connect Button (mobile) -->
          <button
            v-if="!walletConnected"
            @click="handleConnectWallet"
            class="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#7731EA] text-white text-sm font-medium rounded-lg hover:bg-[#6521D0] transition-colors border border-[#7731EA]/20"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 18V19C21 20.1 20.1 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.89 3 5 3H19C20.1 3 21 3.9 21 5V6H12C10.89 6 10 6.9 10 8V16C10 17.1 10.89 18 12 18H21ZM12 16H22V8H12V16ZM16 13.5C15.17 13.5 14.5 12.83 14.5 12C14.5 11.17 15.17 10.5 16 10.5C16.83 10.5 17.5 11.17 17.5 12C17.5 12.83 16.83 13.5 16 13.5Z" fill="currentColor"/>
            </svg>
            Connect wallet
          </button>

          <!-- Wallet Info (mobile) -->
          <template v-else>
            <div class="px-4 py-3 bg-brand-dark-lighter rounded-lg border border-pyth-purple/30">
              <span class="text-xs text-gray-400 block mb-1">Connected Wallet</span>
              <p class="text-white font-mono text-sm">{{ truncatedAddress }}</p>
            </div>
            <button
              @click="handleDisconnect"
              class="w-full px-4 py-3 bg-red-600/20 text-red-400 border border-red-600/50 rounded-lg hover:bg-red-600/30 transition-colors"
            >
              Disconnect Wallet
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- Wallet Connection Modal -->
    <WalletConnectionModal
      :show="showWalletModal"
      @close="closeWalletModal"
      @walletSelected="handleWalletSelected"
    />
  </nav>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { useWalletConnection } from '@/composables/useWalletConnection'
import WalletConnectionModal from './WalletConnectionModal.vue'
import { logos, branding } from '@/assets'

const router = useRouter()
const userStore = useUserStore()
const {
  showWalletModal,
  openWalletModal,
  closeWalletModal,
  handleWalletSelected,
  handleDisconnect: disconnectWallet
} = useWalletConnection()

// State
const mobileMenuOpen = ref(false)

// Computed
const walletConnected = computed(() => userStore.walletConnected)
const walletAddress = computed(() => userStore.walletAddress)
const hasNFT = computed(() => userStore.hasNFT)
const isAuthenticated = computed(() => userStore.isAuthenticated)
const isAdmin = computed(() => userStore.isAdmin)

const truncatedAddress = computed(() => {
  if (!walletAddress.value) return ''
  return `${walletAddress.value.slice(0, 4)}...${walletAddress.value.slice(-4)}`
})

// Methods
function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
}

async function handleConnectWallet() {
  openWalletModal()
  closeMobileMenu()
}

async function handleDisconnect() {
  await disconnectWallet()
  closeMobileMenu()
  router.push('/')
}
</script>

<style scoped>
.navbar {
  box-shadow: 0 4px 6px -1px rgba(130, 71, 229, 0.1);
}

.nav-link {
  @apply text-gray-300 hover:text-white transition-colors font-medium relative;
  padding-bottom: 2px;
}

.nav-link.active {
  @apply text-white;
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #8247E5, #4F46E5);
  border-radius: 2px;
}

.mobile-nav-link {
  @apply block px-4 py-3 text-gray-300 hover:text-white hover:bg-brand-dark-lighter rounded-lg transition-all font-medium;
}

.mobile-nav-link.active {
  @apply text-white bg-brand-dark-lighter border-l-2 border-pyth-purple;
}
</style>
